import { type Block, type Exercise, blockWire, groupsPhrase, newID } from "./models.js";
import { isMainHeader, isPrepHeader, parse, stripListMarker } from "./ruleParser.js";
import { trimWS } from "./text.js";
import {
  adapt,
  asRun,
  countedFly,
  groupWords,
  isRunning,
  ladder,
  namedRun,
  nestedReps,
  oneTimeTrial,
  pairedRuns,
  recordOf,
  splitRuns,
} from "./trackSession.js";
import { foldText, identifier, isPrepLabel, labelKey } from "./trainingWeek.js";
import type { BlockWire, LineLog, PlannedExerciseWire, RecordKind } from "./types.js";

/**
 * DESIGN.md §61.2: the one reading of a definition, worked out by the Worker when its lines change,
 * saved with it and published as is. The phone never reads the coach's words.
 */

export const READING_VERSION = 3;

/** What a set of an item records; `none` is the coach's Not logged: shown as text, never ticked. */
export type ReadingLogs = RecordKind | "none";

export type ReadingFamily = "weights" | "run" | "other";

export interface ReadingItem extends PlannedExerciseWire {
  /** The line it came from, 0-based in the definition's lines after references are expanded. */
  line: number;
  /** Its place among the items one line made (an inline round, a ladder). */
  member?: number;
  logs: ReadingLogs;
  /** The kind is the coach's pick, so it survives his typing. */
  logsSetBy?: "coach";
}

export interface ReadingBlock {
  id: string;
  sets: number;
  note?: string;
  /** Written only when true. */
  isPrep?: true;
  exercises: ReadingItem[];
}

export interface UnreadLine {
  line: number;
  text: string;
}

export interface DefinitionReading {
  version: number;
  family: ReadingFamily;
  blocks: ReadingBlock[];
  unread: UnreadLine[];
}

/** A coach's Logs choice for the items of the line with this text (§61.2). */
export interface Pick {
  text: string;
  member?: number;
  logs: ReadingLogs;
}

export interface DefinitionSource {
  label: string;
  lines: readonly string[];
}

export interface ReadDefinitionOptions {
  /** The week's definitions, so a line naming one is read as its lines. */
  definitions?: readonly DefinitionSource[];
  /** `Day.seed` (§17.4) when reading for a published day; "" for the draft's own reading. */
  seed?: string;
  now?: Date;
  timeZone?: string;
  knownExercises?: string[];
  /**
   * The lines `prior` was read from, when known (even when they are `lines`). Without them a pick
   * is carried only to an item of the same name: a client's reading may be of lines it no longer has.
   */
  priorLines?: readonly string[];
  /** Picks that win over the prior's for the same line: a coach app's `logs` from before §61. */
  picks?: readonly Pick[];
  /** The picks of the definitions this one names, for their lines; its own win. */
  inherited?: readonly Pick[];
}

export const NOT_READ_MARK = "Not read: shown as text, not logged";

const LOGS = new Set<unknown>(["weightReps", "reps", "time", "done", "none"]);

export function isCurrentReading(reading: unknown): reading is DefinitionReading {
  return typeof reading === "object" && reading !== null && (reading as DefinitionReading).version === READING_VERSION && Array.isArray((reading as DefinitionReading).blocks);
}

// MARK: Expanding references

export interface Expanded {
  /** The lines read: the definition's own, with each line naming another definition replaced by its lines. */
  lines: string[];
  /** Per line: it came from a definition labelled as prep. */
  prep: boolean[];
  /** Per line: the definition's own line it is, or that named the definition it came from. */
  own: number[];
}

/** In place, one level deep, as the week's glossary expands (`TrainingWeek`); a label defined twice is both. */
export function expandLines(label: string, lines: readonly string[], definitions: readonly DefinitionSource[] = []): Expanded {
  const key = labelKey(label);
  const index = new Map<string, { label: string; lines: string[] }>();
  for (const definition of definitions) {
    const k = labelKey(definition.label);
    if (k === "" || k === key) continue;
    const found = index.get(k);
    if (found !== undefined) found.lines = [...found.lines, ...definition.lines];
    else index.set(k, { label: definition.label, lines: definition.lines.slice() });
  }
  const out: Expanded = { lines: [], prep: [], own: [] };
  lines.forEach((line, own) => {
    const target = labelKey(line);
    const referenced = target === "" ? undefined : index.get(target);
    if (referenced === undefined) {
      out.lines.push(line);
      out.prep.push(false);
      out.own.push(own);
      return;
    }
    const prep = isPrepLabel(referenced.label);
    for (const inner of referenced.lines) {
      const next = labelKey(inner);
      const deeper = next === key || next === target || next === "" ? undefined : index.get(next);
      for (const l of deeper?.lines ?? [inner]) {
        out.lines.push(l);
        out.prep.push(prep);
        out.own.push(own);
      }
    }
  });
  return out;
}

export function familyOf(label: string, lines: readonly string[]): ReadingFamily {
  if (foldText(label).startsWith("weights") && !isPrepLabel(label)) return "weights";
  return isRunning(lines.slice()) ? "run" : "other";
}

// MARK: Reading

/** `Hep: 500m tempo` is an ordinary line for the whole column (§61.4); the label stays as the item's note. */
function groupLabelled(line: string): { label: string; rest: string } | null {
  const colon = line.indexOf(":");
  if (colon < 0) return null;
  const label = trimWS(line.slice(0, colon));
  if (!groupWords.has(foldText(label))) return null;
  const rest = trimWS(line.slice(colon + 1));
  if (rest === "") return null;
  const indent = /^\s*/u.exec(line)?.[0] ?? "";
  return { label, rest: indent + rest };
}

/** `TrackSession.prepare` for one line, without the group filter. */
function preparedRun(line: string): string[] {
  return [asRun(oneTimeTrial(countedFly(line)))]
    .flatMap(pairedRuns)
    .flatMap(splitRuns)
    .flatMap(ladder)
    .map((l) => namedRun(nestedReps(l)));
}

const REST_LINE = /^(?:full\s+)?(?:rest|recovery|récup(?:ération)?|r\s*:)/iu;

function bodyOf(line: string): string {
  return stripListMarker(trimWS(line)).body;
}

interface Bare {
  expanded: Expanded;
  reading: DefinitionReading;
}

/** The rules' reading of the expanded lines, before any pick and with ids for `seed`. */
function readBare(label: string, lines: readonly string[], options: ReadDefinitionOptions): Bare {
  const expanded = expandLines(label, lines, options.definitions);
  const E = expanded.lines;
  const family = familyOf(label, E);

  const text: string[] = [];
  const origin: (number | null)[] = [];
  const groupOf = new Map<number, string>();
  let inPrepRef = false;
  E.forEach((line, i) => {
    if (expanded.prep[i] && !inPrepRef) {
      text.push("Prep:");
      origin.push(null);
      inPrepRef = true;
    } else if (!expanded.prep[i] && inPrepRef) {
      text.push("");
      origin.push(null);
      inPrepRef = false;
    }
    const labelled = groupLabelled(line);
    if (labelled !== null) groupOf.set(i, labelled.label);
    const read = labelled?.rest ?? line;
    for (const piece of family === "weights" ? [read] : preparedRun(read)) {
      text.push(piece);
      origin.push(i);
    }
  });

  const result = parse(text.join("\n"), {
    knownExercises: options.knownExercises ?? [],
    now: (options.now ?? new Date()).getTime(),
    timeZone: options.timeZone ?? "America/Toronto",
  });
  const parsed: Block[] = family === "weights" ? result.plan.blocks : adapt(result.plan.blocks);
  const exerciseOf = new Map<string, { exercise: Exercise; block: Block }>();
  for (const block of parsed) for (const e of block.exercises) exerciseOf.set(e.id, { exercise: e, block });

  // Which expanded line made each exercise, and its place among that line's.
  const placed = new Map<string, { line: number; member: number }>();
  const madeBy = new Map<number, string[]>();
  const blockStart = new Map<string, number>();
  for (const reading of result.lines) {
    const i = origin[reading.index] ?? null;
    if (i === null) continue;
    if (reading.blockId !== undefined && !blockStart.has(reading.blockId)) blockStart.set(reading.blockId, i);
    if (reading.kind !== "exercise" && reading.kind !== "unread") continue;
    for (const id of reading.exerciseIds) {
      if (placed.has(id) || !exerciseOf.has(id)) continue;
      const list = madeBy.get(i) ?? [];
      list.push(id);
      madeBy.set(i, list);
      placed.set(id, { line: i, member: list.length - 1 });
    }
  }
  for (const block of parsed) {
    for (const e of block.exercises) {
      if (placed.has(e.id)) continue;
      const i = blockStart.get(block.id) ?? 0;
      const list = madeBy.get(i) ?? [];
      list.push(e.id);
      madeBy.set(i, list);
      placed.set(e.id, { line: i, member: list.length - 1 });
    }
  }

  // Per expanded line, what the text lines it became were read as, and whether prep was open.
  const kinds = new Map<number, { kind: string; blockId?: string; prep: boolean }>();
  let prep = false;
  result.lines.forEach((reading) => {
    const body = bodyOf(reading.text);
    if (reading.kind === "blank") prep = false;
    else if (reading.kind === "heading" && isPrepHeader(body)) prep = true;
    else if (reading.kind === "heading" && isMainHeader(body)) prep = false;
    const i = origin[reading.index] ?? null;
    if (i === null || kinds.has(i)) return;
    const entry: { kind: string; blockId?: string; prep: boolean } = { kind: reading.kind, prep };
    if (reading.blockId !== undefined) entry.blockId = reading.blockId;
    kinds.set(i, entry);
  });

  interface Out {
    block: Block;
    items: { exercise: Exercise; line: number; member?: number }[];
    notes: string[];
  }
  const outs = new Map<string, Out>();
  const order: Out[] = [];
  const outOf = (block: Block): Out => {
    let out = outs.get(block.id);
    if (out === undefined) {
      out = { block, items: [], notes: [] };
      outs.set(block.id, out);
      order.push(out);
    }
    return out;
  };
  for (const block of parsed) {
    const out = outOf(block);
    for (const e of block.exercises) {
      const at = placed.get(e.id)!;
      const many = (madeBy.get(at.line)?.length ?? 0) > 1;
      out.items.push(many ? { exercise: e, line: at.line, member: at.member } : { exercise: e, line: at.line });
    }
  }

  const unread: UnreadLine[] = [];
  let loose: Out | null = null;
  let last: Out | null = null;
  E.forEach((line, i) => {
    if (madeBy.has(i)) {
      loose = null;
      last = order.find((out) => out.items.some((item) => item.line === i)) ?? last;
      return;
    }
    const seen = kinds.get(i);
    const body = bodyOf(line);
    const kind = seen?.kind ?? "unread";
    if (body === "" || kind === "blank" || kind === "note" || kind === "block") {
      if (kind !== "note") loose = null;
      return;
    }
    if (kind === "heading" && (isPrepHeader(body) || isMainHeader(body) || body.endsWith(":"))) {
      loose = null;
      return;
    }
    if (REST_LINE.test(body)) {
      if (last !== null) last.notes.push(body);
      return;
    }
    const inPrep = seen?.prep ?? expanded.prep[i] ?? false;
    const counted = /^\p{Nd}/u.test(body);
    if (!inPrep && (family === "weights" || counted)) {
      unread.push({ line: i, text: line });
      return;
    }
    const exercise: Exercise = {
      id: newID(),
      name: body,
      target: null,
      note: null,
      perSide: false,
      isBodyweight: true,
      needsCheck: false,
      recordOverride: "done",
      groups: null,
    };
    const inside = seen?.blockId !== undefined ? parsed.find((b) => b.id === seen.blockId) : undefined;
    if (inside !== undefined) {
      outOf(inside).items.push({ exercise, line: i });
      return;
    }
    if (loose === null || (loose.block.isPrep !== inPrep)) {
      loose = outOf({ id: newID(), sets: 1, note: null, exercises: [], isPrep: inPrep });
    }
    (loose as Out).items.push({ exercise, line: i });
    last = loose;
  });

  const key = labelKey(label);
  const seed = options.seed ?? "";
  const blocks: ReadingBlock[] = [];
  const firstLines: number[] = [];
  for (const out of order) {
    if (out.items.length === 0) continue;
    const items = out.items
      .slice()
      .sort((a, b) => a.line - b.line || (a.member ?? 0) - (b.member ?? 0))
      .map(({ exercise, line, member }): ReadingItem => {
        const wire = blockWire({ ...out.block, exercises: [exercise] }).exercises[0]!;
        const group = groupOf.get(line);
        if (group !== undefined) wire.note = wire.note === undefined ? group : `${group} · ${wire.note}`;
        const logs: RecordKind = out.block.isPrep ? "done" : recordOf(exercise);
        const item: ReadingItem = { ...wire, recordOverride: logs, line, logs };
        if (logs !== "weightReps") item.isBodyweight = true;
        if (member !== undefined) item.member = member;
        return item;
      });
    const note = [out.block.note, ...out.notes].filter((n): n is string => n !== null && n !== "").join(" · ");
    const block: ReadingBlock = { id: "", sets: Math.max(1, out.block.sets), exercises: items };
    if (note !== "") block.note = note;
    if (out.block.isPrep) block.isPrep = true;
    blocks.push(block);
    firstLines.push(items[0]!.line);
  }
  const ranked = blocks.map((block, n) => ({ block, n, line: firstLines[n]! }));
  ranked.sort((a, b) => (family === "weights" ? Number(b.block.isPrep === true) - Number(a.block.isPrep === true) : 0) || a.line - b.line || a.n - b.n);
  const reading: DefinitionReading = { version: READING_VERSION, family, blocks: ranked.map((r) => r.block), unread };
  return { expanded, reading: withIDs(reading, key, seed) };
}

function itemKey(item: { line: number; member?: number }): string {
  return `${item.line}|${item.member ?? ""}`;
}

/** Block and item ids from the seed, the label and the line: the same reading is the same bytes. */
function withIDs(reading: DefinitionReading, key: string, seed: string): DefinitionReading {
  const used = new Set<string>();
  const unique = (base: string): string => {
    let name = base;
    for (let n = 2; used.has(name); n += 1) name = `${base}|${n}`;
    used.add(name);
    return identifier(name);
  };
  return {
    ...reading,
    blocks: reading.blocks.map((block) => {
      const first = block.exercises.reduce((low, item) => Math.min(low, item.line), Number.MAX_SAFE_INTEGER);
      return {
        ...block,
        id: unique(`${seed}|${key}|block|${first}`),
        exercises: block.exercises.map((item) => ({ ...item, id: unique(`${seed}|${key}|${itemKey(item)}`) })),
      };
    }),
  };
}

// MARK: The coach's picks

function pickKey(text: string, member: number | undefined): string {
  return `${trimWS(text)}\u0000${member ?? ""}`;
}

/** The items with the picks for their lines' text: the n-th item of a text takes its n-th pick, or its last. */
function applyPicks(reading: DefinitionReading, lines: readonly string[], picks: readonly Pick[]): DefinitionReading {
  if (picks.length === 0) return reading;
  const byKey = new Map<string, Pick[]>();
  for (const pick of picks) {
    const k = pickKey(pick.text, pick.member);
    byKey.set(k, [...(byKey.get(k) ?? []), pick]);
  }
  const taken = new Map<string, number>();
  return {
    ...reading,
    blocks: reading.blocks.map((block) => ({
      ...block,
      exercises: block.exercises.map((item) => {
        const text = lines[item.line];
        if (text === undefined) return item;
        const k = pickKey(text, item.member);
        const found = byKey.get(k);
        if (found === undefined) return item;
        const n = taken.get(k) ?? 0;
        taken.set(k, n + 1);
        const pick = found[Math.min(n, found.length - 1)]!;
        const picked: ReadingItem = { ...item, logs: pick.logs, logsSetBy: "coach" };
        if (pick.logs !== "none") {
          picked.recordOverride = pick.logs;
          picked.isBodyweight = pick.logs !== "weightReps";
        }
        return picked;
      }),
    })),
  };
}

/** Picks that come later replace earlier ones of the same line text and member. */
export function mergePicks(...lists: readonly (readonly Pick[])[]): Pick[] {
  const byKey = new Map<string, Pick[]>();
  for (const list of lists) {
    const fresh = new Map<string, Pick[]>();
    for (const pick of list) {
      const k = pickKey(pick.text, pick.member);
      fresh.set(k, [...(fresh.get(k) ?? []), pick]);
    }
    for (const [k, value] of fresh) byKey.set(k, value);
  }
  return [...byKey.values()].flat();
}

/**
 * The coach's picks in a reading, each with the text of its line. `readFrom` are the lines it may
 * have been read from, likeliest first: a pick is placed on the first whose fresh reading has an
 * item of the same name at its line and member, else on the one item of that name and member in
 * the first, else, when `certain` that the first is what it was read from, on that line of it.
 * Anything not shaped like a pick is skipped, so a reading a client sent is never trusted for more.
 */
export function picksOf(
  label: string,
  prior: unknown,
  readFrom: readonly (readonly string[])[],
  options: ReadDefinitionOptions = {},
  known?: Map<string, Bare>,
  certain = false,
): Pick[] {
  const items = pickItems(prior);
  if (items.length === 0 || readFrom.length === 0) return [];
  const cache = known ?? new Map<string, Bare>();
  const bare = (lines: readonly string[]): Bare => {
    const k = lines.join("\n");
    let found = cache.get(k);
    if (found === undefined) {
      found = readBare(label, lines, { ...options, seed: "" });
      cache.set(k, found);
    }
    return found;
  };
  const out: Pick[] = [];
  for (const item of items) {
    let text: string | undefined;
    for (const lines of readFrom) {
      const read = bare(lines);
      const same = read.reading.blocks.some((b) => b.exercises.some((e) => e.line === item.line && e.member === item.member && e.name === item.name));
      if (same) {
        text = read.expanded.lines[item.line];
        break;
      }
    }
    if (text === undefined) {
      const first = bare(readFrom[0]!);
      const named = first.reading.blocks.flatMap((b) => b.exercises).filter((e) => e.member === item.member && e.name === item.name && item.name !== "");
      if (named.length === 1) text = first.expanded.lines[named[0]!.line];
      else if (certain) text = first.expanded.lines[item.line];
    }
    if (text === undefined || trimWS(text) === "") continue;
    const pick: Pick = { text, logs: item.logs };
    if (item.member !== undefined) pick.member = item.member;
    out.push(pick);
  }
  return out;
}

function pickItems(prior: unknown): { line: number; member?: number; name: string; logs: ReadingLogs }[] {
  if (typeof prior !== "object" || prior === null || !Array.isArray((prior as { blocks?: unknown }).blocks)) return [];
  const out: { line: number; member?: number; name: string; logs: ReadingLogs }[] = [];
  for (const block of (prior as { blocks: unknown[] }).blocks.slice(0, 400)) {
    const exercises = typeof block === "object" && block !== null ? (block as { exercises?: unknown }).exercises : undefined;
    if (!Array.isArray(exercises)) continue;
    for (const raw of exercises.slice(0, 400)) {
      if (typeof raw !== "object" || raw === null) continue;
      const e = raw as Record<string, unknown>;
      if (e.logsSetBy !== "coach" || !LOGS.has(e.logs)) continue;
      if (typeof e.line !== "number" || !Number.isInteger(e.line) || e.line < 0) continue;
      if (e.member !== undefined && (typeof e.member !== "number" || !Number.isInteger(e.member) || e.member < 0)) continue;
      const item: { line: number; member?: number; name: string; logs: ReadingLogs } = { line: e.line, name: typeof e.name === "string" ? e.name : "", logs: e.logs as ReadingLogs };
      if (e.member !== undefined) item.member = e.member as number;
      out.push(item);
    }
  }
  return out;
}

/** §61.2: a 1.16 coach app's positional `logs` as picks by their lines' text. */
export function picksFromLogs(logs: readonly LineLog[] | undefined): Pick[] {
  if (!Array.isArray(logs)) return [];
  return logs.flatMap((log): Pick[] => {
    if (typeof log !== "object" || log === null || typeof log.text !== "string" || trimWS(log.text) === "" || !LOGS.has(log.record) || log.record === ("none" as unknown)) return [];
    const pick: Pick = { text: log.text, logs: log.record };
    if (Number.isInteger(log.member) && log.member! >= 0) pick.member = log.member!;
    return [pick];
  });
}

/**
 * `readDefinition(label, lines, prior?, options)` (§61.2): the definition read with the rules, its
 * Logs picks carried over from `prior` by their lines' text, then `options.picks` over those.
 */
export function readDefinition(label: string, lines: readonly string[], prior?: unknown, options: ReadDefinitionOptions = {}): DefinitionReading {
  const cache = new Map<string, Bare>();
  const base = readBare(label, lines, { ...options, seed: "" });
  cache.set(lines.join("\n"), base);
  const readFrom = options.priorLines !== undefined && options.priorLines.join("\n") !== lines.join("\n") ? [options.priorLines, lines] : [lines];
  const certain = options.priorLines !== undefined;
  const picks = mergePicks(options.inherited ?? [], prior === undefined || prior === null ? [] : picksOf(label, prior, readFrom, options, cache, certain), options.picks ?? []);
  const seed = options.seed ?? "";
  const reading = seed === "" ? base.reading : withIDs(base.reading, labelKey(label), seed);
  return applyPicks(reading, base.expanded.lines, picks);
}

/** The picks a reading holds, as `Pick`s on the lines it was read from. */
export function picksIn(reading: DefinitionReading, expandedLines: readonly string[]): Pick[] {
  const out: Pick[] = [];
  for (const block of reading.blocks) {
    for (const item of block.exercises) {
      const text = expandedLines[item.line];
      if (item.logsSetBy !== "coach" || text === undefined) continue;
      const pick: Pick = { text, logs: item.logs };
      if (item.member !== undefined) pick.member = item.member;
      out.push(pick);
    }
  }
  return out;
}

// MARK: What the planner shows, and what is published

/** The planner's marks on the expanded lines: each unread line (§32.4). */
export function readingMarks(reading: DefinitionReading): { line: number; words: string }[] {
  return reading.unread.map((u) => ({ line: u.line, words: NOT_READ_MARK }));
}

export interface PublishedBlockWire extends BlockWire {
  loggedFrom?: string;
}

/**
 * §61.4: the reading as a published day's blocks, ids made from the day's seed. An item set to Not
 * logged and an unread line become words in their block's note; a block left with nothing to tick
 * gives its words to the next block (else the one before).
 */
export function publishedBlocks(
  reading: DefinitionReading,
  options: { label: string; seed: string; lines: readonly string[]; loggedFrom?: string },
): PublishedBlockWire[] {
  const seeded = withIDs(reading, labelKey(options.label), options.seed);
  const words = (line: number, fallback: string): string => {
    const text = options.lines[line];
    const body = text === undefined ? "" : bodyOf(text);
    return body === "" ? fallback : body;
  };
  const extra: string[][] = seeded.blocks.map(() => []);
  const firstLine = seeded.blocks.map((b) => b.exercises.reduce((low, e) => Math.min(low, e.line), Number.MAX_SAFE_INTEGER));
  for (const u of reading.unread) {
    let at = 0;
    let best = -1;
    seeded.blocks.forEach((block, n) => {
      for (const e of block.exercises) if (e.line < u.line && e.line > best) [best, at] = [e.line, n];
    });
    if (seeded.blocks.length > 0) extra[best < 0 ? firstLine.indexOf(Math.min(...firstLine)) : at]!.push(trimWS(u.text));
  }
  const built = seeded.blocks.map((block, n) => {
    const said = new Set<number>();
    const notes: string[] = block.note !== undefined ? [block.note] : [];
    const exercises: PlannedExerciseWire[] = [];
    for (const item of block.exercises) {
      if (item.logs === "none") {
        if (!said.has(item.line)) notes.push(words(item.line, item.name));
        said.add(item.line);
        continue;
      }
      const { logs: _logs, ...rest } = item;
      const wire: PlannedExerciseWire & Record<string, unknown> = { ...rest, recordOverride: item.logs };
      const phrase = groupsPhrase({
        ...(wire as unknown as Exercise),
        target: wire.target ? { amount: wire.target.amount, upper: wire.target.upper ?? null, unit: wire.target.unit } : null,
        groups: wire.groups ?? null,
      });
      if (phrase !== null) wire.note = [phrase, wire.note].filter((x): x is string => x !== undefined).join(" · ");
      exercises.push(wire);
    }
    return { block, notes: [...notes, ...extra[n]!], exercises };
  });
  const out: PublishedBlockWire[] = [];
  let carried: string[] = [];
  built.forEach(({ block, notes, exercises }) => {
    if (exercises.length === 0) {
      carried.push(...notes);
      return;
    }
    const all = [...carried, ...notes].filter((x) => x !== "");
    carried = [];
    const wire = { id: block.id, sets: block.sets } as PublishedBlockWire;
    if (all.length > 0) wire.note = all.join(" · ");
    wire.exercises = exercises;
    wire.isPrep = block.isPrep === true;
    if (options.loggedFrom !== undefined) wire.loggedFrom = options.loggedFrom;
    out.push(wire);
  });
  if (carried.length > 0 && out.length > 0) {
    const lastBlock = out[out.length - 1]!;
    lastBlock.note = [lastBlock.note, ...carried].filter((x): x is string => x !== undefined && x !== "").join(" · ");
  }
  return out;
}
